import PropTypes from 'prop-types';
import React, { useContext, useState, useEffect } from 'react';
import {
    PromotionalAdConsumer,
    PromotionalAdProvider,
} from '../../../../contexts/PromotionalAdContext';
import { EmitMetric } from '../../../Analytics/VisibilityPixel/VisibilityPixel';
import Button from '../../../Input/Button/Button';
import InfiniteScrollWrapper from '../../../Layout/InfiniteScrollWrapper/InfiniteScrollWrapper';
import PromotionalAd from '../../../PromoionalAd/PromotionalAd';
import LoadingShineComponent from '../LoadingShineComponent/LoadingShineComponent';
import UnifiedPostCard from '../../../UnifiedPostCard/UnifiedPostCard';
import {
    CommunityContext,
    PageContext,
} from '../../../../contexts/ContextCreator';

import './CommunityFeeds.scss';
import RankingComponent from '../RankingComponent/RankingComponent';
import ZeroStateNeedLove from '../RankingComponent/ZeroStateNeedLove';
import FeedFilterBar from '../../../Community/FeedFilterBar/FeedFilterBar';
import useOptimize from '../../../../utilities/hooks/useOptimize';

export const LOAD_MORE_BUTTON = 'load-more';
export const REDIRECT_TO_FEED = 'redirect';
export const NO_BUTTON = 'no-button';
export const TOPIC_LOAD_MORE_BUTTON = 'topic-load-more';
const optimizeExperimentId = 'y5XyzSRQQwOoA6bmj2hgPA';

const disableFilter = true;

const CommunityFeeds = (props) => {
    const [initialLoad, setInitialLoad] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const {
        location,
        feedGroup,
        feedGroupId,
        communityGroupHash,
        metricEventType,
        hasNext,
        feedLastResultCount,
        infiniteScroll,
        feedType,
        loadMoreButtonText,
        endOfFeedButtonType,
        topicContentTotal,
        topicLoadMoreObject,
        topicMetrics,
    } = props;

    const { pageProps, getPageKey, isMobile } = useContext(PageContext);
    const { content, fetchMoreContent, sort } = useContext(CommunityContext);
    const pageKey = getPageKey();
    const [variant] = useOptimize(optimizeExperimentId);

    const setLoadingAndInitialLoading = (
        isLoadingValue = false,
        initialLoadValue = false
    ) => {
        setIsLoading(isLoadingValue);
        setInitialLoad(initialLoadValue);
    };

    useEffect(() => {
        fetchMoreContent({
            feedGroup,
            feedGroupId,
            setIsLoading: setLoadingAndInitialLoading,
            hash: communityGroupHash,
        });
        if (!infiniteScroll) {
            setLoadingAndInitialLoading();
        }
    }, [
        fetchMoreContent,
        feedGroup,
        feedGroupId,
        communityGroupHash,
        infiniteScroll,
    ]);

    const handleFetchMoreContent = async () => {
        setLoadingAndInitialLoading(true);
        try {
            if (!isLoading && hasNext && feedLastResultCount !== 0) {
                await fetchMoreContent({
                    hash: communityGroupHash,
                    feedGroup,
                    feedGroupId,
                    setIsLoading: setLoadingAndInitialLoading,
                    direction: 'up',
                });
                EmitMetric({
                    misc_event_type: metricEventType,
                    misc_event_count: 1,
                });
            }
        } catch (e) {
            setLoadingAndInitialLoading();
        }
    };

    const renderFeeds = () => {
        return (
            <PromotionalAdConsumer>
                {(adContext) => {
                    const { masterFeedAdConfig } = pageProps;
                    const components = [];
                    let adPosition = 0;

                    /*
                    Location variable below added for backwards compatibility.
                    The feedType property has been defining the location of content which is problematic.
                    Only if no location is passed as a prop to this component will the content location fall back to feed type
                    -MSH 071219
                */
                    // if no posts need love, show notification
                    if (
                        sort === 'NEED_LOVE' &&
                        feedGroup === 'GlobalFeed' &&
                        feedType === 'masterFeed' &&
                        !initialLoad &&
                        !content?.length
                    ) {
                        return <ZeroStateNeedLove />;
                    }

                    // @TODO should refactor using .map()
                    for (let i = 0; i < content.length; i += 1) {
                        const contentSelected = content[i];
                        if (
                            contentSelected.moderationOption?.status?.type !==
                            'delete'
                        ) {
                            const trueKey = `${contentSelected.hash}-${i}`;
                            components.push(
                                <UnifiedPostCard
                                    key={trueKey}
                                    {...contentSelected}
                                    location={location || feedType}
                                    feedType={feedType}
                                />
                            );
                        }

                        if (
                            masterFeedAdConfig &&
                            (i + 1) % masterFeedAdConfig.cadence === 0 &&
                            adContext?.ads?.length > 0
                        ) {
                            components.push(
                                <PromotionalAd
                                    selectedAd={adContext.getAdAtPosition(
                                        adPosition,
                                        pageKey
                                    )}
                                    key={adPosition}
                                />
                            );
                            adPosition += 1;
                        }
                    }
                    if (infiniteScroll) {
                        return (
                            <InfiniteScrollWrapper
                                pageStart={0}
                                loadMore={handleFetchMoreContent}
                                hasMore={hasNext}
                                isLoading={isLoading}
                                useWindow
                                threshold={500}
                            >
                                {components}
                            </InfiniteScrollWrapper>
                        );
                    }
                    return components;
                }}
            </PromotionalAdConsumer>
        );
    };

    const loadShowMoreButton = () => {
        if (endOfFeedButtonType === REDIRECT_TO_FEED) {
            return (
                <div className="loadMoreButtonDiv">
                    <a href="/community/feed">
                        <Button type="solid" value={loadMoreButtonText} />
                    </a>
                </div>
            );
        }

        if (
            endOfFeedButtonType === TOPIC_LOAD_MORE_BUTTON &&
            content &&
            content.length < topicContentTotal
        ) {
            return (
                <div className="loadMoreButtonDiv">
                    <Button
                        type="solid"
                        value={loadMoreButtonText}
                        onClick={() => {
                            fetchMoreContent(topicLoadMoreObject);
                            EmitMetric(topicMetrics);
                        }}
                    />
                </div>
            );
        }
        if (endOfFeedButtonType === NO_BUTTON) {
            return null;
        }
        return null;
    };

    const loadRankingComponent = () => {
        return variant !== 1 ? (
            <RankingComponent setIsLoading={setLoadingAndInitialLoading} />
        ) : (
            <FeedFilterBar setIsLoading={setLoadingAndInitialLoading} />
        );
    };

    return (
        <div className="component-Content-Feed-CommunityFeeds">
            <PromotionalAdProvider pageKey={pageKey} isMobile={isMobile}>
                <React.Fragment>
                    {feedType === 'masterFeed' &&
                    feedGroup === 'GlobalFeed' &&
                    !disableFilter
                        ? loadRankingComponent()
                        : null}
                    {renderFeeds()}
                    {loadShowMoreButton()}
                    {initialLoad ? (
                        <React.Fragment>
                            <LoadingShineComponent />
                            <LoadingShineComponent />
                            <LoadingShineComponent />
                        </React.Fragment>
                    ) : null}
                    {isLoading && feedLastResultCount !== 0 ? (
                        <LoadingShineComponent />
                    ) : null}
                </React.Fragment>
            </PromotionalAdProvider>
        </div>
    );
};

CommunityFeeds.propTypes = {
    feedType: PropTypes.oneOf(['miniFeed', 'masterFeed']).isRequired,
    feedGroup: PropTypes.oneOf([
        'CommunityDiscussion',
        'CommunityLink',
        'CommunityTopic',
        'CommunityTag',
        'EditorialContent',
        'GlobalFeed',
        'Company',
        'GroupFeed',
    ]).isRequired,
    feedGroupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    loadMoreButtonText: PropTypes.string,
    createContentEnabled: PropTypes.bool,
    /**
     * if true load more button if false, redirect button
     */
    hasNext: PropTypes.bool.isRequired,
    feedLastResultCount: PropTypes.number,
    endOfFeedButtonType: PropTypes.oneOf([
        LOAD_MORE_BUTTON,
        REDIRECT_TO_FEED,
        NO_BUTTON,
    ]),
    metricEventType: PropTypes.string,
    useCommonCardShell: PropTypes.bool,
    /**
     * group hash required when fetching a group feed
     */
    communityGroupHash: PropTypes.string,
    /**
     * the location of the feed (for metrics purposes)
     */
    location: PropTypes.oneOf([
        'masterFeed',
        'groupFeed',
        'topic-load-more',
        'homePage',
    ]),
    hideReactionBar: PropTypes.bool,
    infiniteScroll: PropTypes.bool,
    fetchMoreContent: PropTypes.func,
    topicContentTotal: PropTypes.number,
    topicMetrics: PropTypes.shape({
        misc_event_count: PropTypes.number,
        misc_event_type: PropTypes.string,
    }),
    topicLoadMoreObject: PropTypes.shape({
        topicUrl: PropTypes.string,
    }),
    userRelation: PropTypes.shape({
        role: PropTypes.string,
    }),
};

CommunityFeeds.defaultProps = {
    infiniteScroll: true,
    feedGroupId: '',
    loadMoreButtonText: 'Load more',
    createContentEnabled: true,
    endOfFeedButtonType: LOAD_MORE_BUTTON,
    metricEventType: 'community-feed-load-more',
    hideReactionBar: false,
    useCommonCardShell: true,
    communityGroupHash: '',
    location: 'masterFeed',
    topicMetrics: {},
    fetchMoreContent: () => {},
    topicContentTotal: 0,
    topicLoadMoreObject: {},
    feedLastResultCount: 0,
    userRelation: {},
};

export default React.memo(CommunityFeeds);
